import type { SalesCategoryPrimaryLevel, SalesCategorySecondaryLevel } from "common/types/salesCategoryTypesEnum";
import { SalesCategory } from "common/types/salesCategoryTypesEnum";
import { assertIsNotNull } from "common/utils/assertOfType";
import type {
  BuilderSalesCategoryCustomization,
  SalesCategoryHierarchy,
  SalesCategoryHierarchyPrimaryLevel,
  SalesCategoryHierarchySecondaryLevel,
} from "./salesCategoryTypes";

export const specialSalesCategories = [
  SalesCategory.UNCATEGORIZED,
  SalesCategory.OPERATIONS,
  SalesCategory.EXTRA_OPTIONS,
];

export const defaultSalesCategoryHierarchy: SalesCategoryHierarchy = {
  primaryLevels: [
    {
      key: "DESIGN",
      secondaryLevels: [
        { key: "ELEVATIONS", salesCategory: SalesCategory.DESIGN_ELEVATIONS },
        { key: "PALETTES", salesCategory: SalesCategory.DESIGN_PALETTES },
        { key: "FOUNDATION", salesCategory: SalesCategory.DESIGN_FOUNDATION },
      ],
    },
    {
      key: "ENTRY",
      secondaryLevels: [
        { key: "GENERAL", salesCategory: SalesCategory.ENTRY_GENERAL },
        { key: "PORCH", salesCategory: SalesCategory.ENTRY_PORCH },
        { key: "OWNERS_ENTRY", salesCategory: SalesCategory.ENTRY_OWNERS_ENTRY },
        { key: "STAIRS", salesCategory: SalesCategory.ENTRY_STAIRS },
      ],
    },
    {
      key: "LIVING",
      secondaryLevels: [
        { key: "GENERAL", salesCategory: SalesCategory.LIVING_GENERAL },
        { key: "FLEXIBLE", salesCategory: SalesCategory.LIVING_FLEXIBLE },
        { key: "DINING", salesCategory: SalesCategory.LIVING_DINING },
        { key: "HALL", salesCategory: SalesCategory.LIVING_HALL },
      ],
    },
    {
      key: "KITCHEN",
      secondaryLevels: [
        { key: "GENERAL", salesCategory: SalesCategory.KITCHEN_GENERAL },
        { key: "CABINETS", salesCategory: SalesCategory.KITCHEN_CABINETS },
        { key: "APPLIANCES", salesCategory: SalesCategory.KITCHEN_APPLIANCES },
      ],
    },
    {
      key: "BED_BATH",
      secondaryLevels: [
        { key: "GENERAL", salesCategory: SalesCategory.BED_BATH_GENERAL },
        { key: "OWNERS_SUITE", salesCategory: SalesCategory.BED_BATH_OWNERS_SUITE },
        { key: "SECONDARY_BATH", salesCategory: SalesCategory.BED_BATH_SECONDARY_BATH },
        { key: "SECONDARY_BED", salesCategory: SalesCategory.BED_BATH_SECONDARY_BED },
      ],
    },
    {
      key: "OUTDOOR",
      secondaryLevels: [
        { key: "GENERAL", salesCategory: SalesCategory.OUTDOOR_GENERAL },
        { key: "LIVING", salesCategory: SalesCategory.OUTDOOR_LIVING },
        { key: "PATIO", salesCategory: SalesCategory.OUTDOOR_PATIO },
      ],
    },
    {
      key: "UTILITY",
      secondaryLevels: [
        { key: "GENERAL", salesCategory: SalesCategory.UTILITY_GENERAL },
        { key: "GARAGE", salesCategory: SalesCategory.UTILITY_GARAGE },
        { key: "FLEXIBLE", salesCategory: SalesCategory.UTILITY_FLEXIBLE },
        { key: "LAUNDRY", salesCategory: SalesCategory.UTILITY_LAUNDRY },
      ],
    },
    {
      key: "MISC",
      secondaryLevels: [{ key: "GENERAL", salesCategory: SalesCategory.MISC_GENERAL }],
    },
  ],
};

export const referenceKeyPrefix = "_";
export function isReferenceKey(key: string): boolean {
  return key.startsWith(referenceKeyPrefix);
}

/**
 * Sales categories are based on secondary level categories.
 * This is returning a list of those names (from salesCategory field), appending with the special sales categories if flag is true
 * @param hierarchy
 * @param includeSpecial
 * @returns
 */
export function salesCategoryNamesInHierarchy(
  hierarchy: SalesCategoryHierarchy | undefined,
  includeSpecial: boolean = false,
): string[] {
  const primaryLevels = hierarchy?.primaryLevels ?? defaultSalesCategoryHierarchy.primaryLevels;
  const standardCategories = primaryLevels.reduce(
    (res, primaryLevel) =>
      res.concat(primaryLevel.secondaryLevels.map((secondaryLevel) => secondaryLevel.salesCategory)),
    [] as string[],
  );
  return includeSpecial ? (specialSalesCategories as string[]).concat(standardCategories) : standardCategories;
}

export function levelsInHierarchyBySalesCategory(
  secondarySalesCategory: string,
  hierarchy: SalesCategoryHierarchy | undefined,
): {
  primaryLevel: SalesCategoryHierarchyPrimaryLevel | null;
  secondaryLevel: SalesCategoryHierarchySecondaryLevel | null;
} {
  const primaryLevels = hierarchy?.primaryLevels ?? defaultSalesCategoryHierarchy.primaryLevels;
  for (const primaryLevel of primaryLevels) {
    if (primaryLevel?.secondaryLevels) {
      const secondaryLevel = primaryLevel.secondaryLevels.find(
        (secondaryLevel) => secondaryLevel.salesCategory === secondarySalesCategory,
      );
      if (secondaryLevel) {
        return { primaryLevel, secondaryLevel };
      }
    }
  }
  return { primaryLevel: null, secondaryLevel: null };
}

export function sanitizeSalesCategory(salesCategory: string, hierarchy: SalesCategoryHierarchy | undefined): string {
  if (salesCategory) {
    if (specialSalesCategories.includes(salesCategory as SalesCategory)) {
      return salesCategory;
    }
    const levels = levelsInHierarchyBySalesCategory(salesCategory, hierarchy);
    if (levels.primaryLevel && levels.secondaryLevel) {
      return salesCategory;
    }
  }

  return SalesCategory.UNCATEGORIZED;
}

export const primaryLevelPrettyNames: Required<Record<SalesCategoryPrimaryLevel, string>> = {
  BED_BATH: "Bed & Bath",
  DESIGN: "Design",
  ENTRY: "Entry",
  EXTRA_OPTIONS: "Extra Options",
  KITCHEN: "Kitchen",
  LIVING: "Living",
  MISC: "Misc",
  OPERATIONS: "Operations",
  OUTDOOR: "Outdoor",
  UNCATEGORIZED: "Uncategorized",
  UTILITY: "Utility",
};

export function prettifyPrimaryLevelName(primaryLevel: SalesCategoryHierarchyPrimaryLevel): string | undefined {
  const { key: primary } = primaryLevel;
  if (isReferenceKey(primary)) {
    return primaryLevel.prettyName;
  } else {
    return primaryLevel.prettyName ?? primaryLevelPrettyNames[primary as SalesCategoryPrimaryLevel];
  }
}

const secondaryLevelPrettyNames: Required<Record<SalesCategorySecondaryLevel, string>> = {
  APPLIANCES: "Appliances",
  CABINETS: "Cabinets",
  DINING: "Dining",
  ELEVATIONS: "Elevations",
  FLEXIBLE: "Flexible",
  FOUNDATION: "Foundation",
  GARAGE: "Garage",
  GENERAL: "General",
  HALL: "Hall",
  LAUNDRY: "Laundry",
  LIVING: "Living",
  OWNERS_ENTRY: "Owner's Entry",
  OWNERS_SUITE: "Owner's Suite",
  PALETTES: "Palettes",
  PATIO: "Patio",
  PORCH: "Porch",
  SECONDARY_BATH: "Secondary Bath",
  SECONDARY_BED: "Secondary Bed",
  STAIRS: "Stairs",
};

export function prettifySecondaryLevelName(secondaryLevel: SalesCategoryHierarchySecondaryLevel): string | undefined {
  const { key: secondary, salesCategory } = secondaryLevel;

  if (isReferenceKey(salesCategory)) {
    return secondaryLevel.prettyName;
  } else {
    return secondaryLevel.prettyName ?? secondaryLevelPrettyNames[secondary as SalesCategorySecondaryLevel];
  }
}

export const prettifySalesCategory = (
  salesCategory: string,
  customization: BuilderSalesCategoryCustomization | undefined,
): string | undefined => {
  const hierarchy = customization?.hierarchy ?? defaultSalesCategoryHierarchy;
  const finalSalesCategory = sanitizeSalesCategory(salesCategory, hierarchy);

  if (specialSalesCategories.includes(finalSalesCategory as SalesCategory)) {
    return prettifyPrimaryLevelName({ key: finalSalesCategory, secondaryLevels: [] });
  } else {
    const { primaryLevel, secondaryLevel } = levelsInHierarchyBySalesCategory(finalSalesCategory, hierarchy);
    assertIsNotNull(primaryLevel);
    assertIsNotNull(secondaryLevel);
    return `${prettifyPrimaryLevelName(primaryLevel)} - ${prettifySecondaryLevelName(secondaryLevel)}`;
  }
};

/**
 * Maps each sales-category key to its display name, so callers (the catalog UI and CSV export)
 * resolve keys the same way. The stored `salesCategory` is a key (`LIVING_GENERAL`, or a custom
 * reference key like `_a46f`); names for custom keys live only in the builder's
 * `salesCategoryCustomization.hierarchy`. Keys absent from the map are the caller's concern.
 */
export function buildSalesCategoryNameMap(
  customization: BuilderSalesCategoryCustomization | undefined,
): Record<string, string> {
  const hierarchy = customization?.hierarchy ?? defaultSalesCategoryHierarchy;
  return salesCategoryNamesInHierarchy(hierarchy, true).reduce<Record<string, string>>((acc, key) => {
    acc[key] = prettifySalesCategory(key, customization) ?? "Unlabeled";
    return acc;
  }, {});
}
